import React from 'react';
import { motion } from 'framer-motion';
import { HeartIcon, PauseIcon, PlayIcon, XIcon } from 'lucide-react';
import type { Track } from '../types/music';
import { usePlayer } from '../contexts/PlayerContext';
import { useLibrary } from '../contexts/LibraryContext';
import { formatTime } from '../utils/format';
import { NowPlayingBars } from './NowPlayingBars';
import { AddToPlaylistMenu } from './AddToPlaylistMenu';
interface TrackTableProps {
  tracks: Track[];
  /** Hide the album column on narrow surfaces where it would crowd the row. */
  showAlbum?: boolean;
  /** Supplied by playlist views to offer row removal. */
  onRemove?: (track: Track) => void;
}
export function TrackTable({
  tracks,
  showAlbum = true,
  onRemove
}: TrackTableProps) {
  const {
    play,
    currentTrack,
    isPlaying
  } = usePlayer();
  const {
    isFavorite,
    toggleFavorite
  } = useLibrary();
  return <table className="w-full border-collapse text-left text-sm" data-id="element-221" data-mp="bm3mwozr3pbg">
      <thead data-id="element-222" data-mp="gnj6sdq6zps9">
        <tr className="border-b border-ink-line text-xs text-muted" data-id="element-223" data-mp="gkx1kkqrxmd7">
          <th scope="col" className="w-10 py-2 pl-3 font-medium" data-id="element-224" data-mp="1nr5x00oykuj3">
            #
          </th>
          <th scope="col" className="py-2 font-medium" data-id="element-225" data-mp="1iq8e1lw4jwgj">
            Title
          </th>
          {showAlbum && <th scope="col" className="hidden py-2 font-medium lg:table-cell" data-id="element-226" data-mp="1dsp0ypb5xpq6">
              Album
            </th>}
          <th scope="col" className="hidden py-2 font-medium sm:table-cell" data-id="element-227" data-mp="19sy3k2r4sbco">
            Genre
          </th>
          <th scope="col" className="w-20 py-2 pr-3 text-right font-medium" data-id="element-228" data-mp="21hrg9qibr6hi">
            Preview
          </th>
          <th scope="col" className="w-24 py-2 pr-3" data-id="element-229" data-mp="1wqt0qt55ucfb">
            <span className="sr-only" data-id="element-230" data-mp="1wp85t2exorgx">Track actions</span>
          </th>
        </tr>
      </thead>
      <tbody data-id="element-231" data-mp="1sp7eih6qklsg">
        {tracks.map((track, i) => {
        const active = currentTrack?.id === track.id;
        const playingThis = active && isPlaying;
        return <motion.tr key={track.id} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1],
          delay: Math.min(i, 9) * 0.025
        }} className={`group border-b border-ink-line/50 transition-colors duration-150 ease-night hover:bg-ink-raised/70 ${active ? 'bg-ink-raised/50' : ''}`} data-id="element-232" data-mp="2kfu07qw0md8g" data-mpi={i}>
              <td className="py-2.5 pl-3 align-middle" data-id="element-233" data-mp="3vxzog8ti6xcv">
                <button type="button" onClick={() => play(tracks, i)} aria-label={playingThis ? `Pause ${track.title}` : `Play ${track.title} by ${track.artist}`} className="flex h-7 w-7 items-center justify-center rounded-full text-muted transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-234" data-mp="3vweuakb3pgfj">
                  {playingThis ? <PauseIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-235" data-mp="1p706babpj980" /> : <>
                      <span className={`tabular-nums ${active ? 'text-gold' : ''} group-hover:hidden`} data-id="element-236" data-mp="39n810nbp7e73">{i + 1}</span>
                      <PlayIcon className="hidden h-4 w-4 group-hover:block" aria-hidden="true" data-id="element-237" data-mp="3jf40o2tfncdf" />
                    </>}
                </button>
              </td>
              <td className="py-2.5 pr-4 align-middle" data-id="element-238" data-mp="3emuf0yzcoygy">
                <div className="flex items-center gap-3" data-id="element-239" data-mp="3ser3mt1t8q8l">
                  {track.artworkSmall ? <img src={track.artworkSmall} alt="" loading="lazy" className="h-10 w-10 flex-none rounded object-cover" data-id="element-240" data-mp="3odp2u7s8w64i" /> : <div className="h-10 w-10 flex-none rounded bg-ink-raised" data-id="element-241" data-mp="27zyuupy08xpl" />}
                  <div className="min-w-0" data-id="element-242" data-mp="231dvesqkui27">
                    <p className={`truncate font-medium ${active ? 'text-gold' : 'text-cream'}`} data-id="element-243" data-mp="232ox8n0gpfqo">{track.title}</p>
                    <p className="truncate text-xs text-muted" data-id="element-244" data-mp="24i1hbjyyugh6">{track.artist}</p>
                  </div>
                  {active && <NowPlayingBars playing={playingThis} data-id="element-245" data-mp="29j799fksxzpn" />}
                </div>
              </td>
              {showAlbum && <td className="hidden max-w-[16rem] truncate py-2.5 pr-4 align-middle text-muted lg:table-cell" data-id="element-246" data-mp="2jg3jbpqcmtwb">
                  {track.album}
                </td>}
              <td className="hidden py-2.5 pr-4 align-middle text-muted sm:table-cell" data-id="element-247" data-mp="1qqwde5okf211">{track.genre}</td>
              <td className="py-2.5 pr-3 text-right align-middle tabular-nums text-muted" data-id="element-248" data-mp="1vq99zi58nngt">{formatTime(30)}</td>
              <td className="py-2.5 pr-3 align-middle" data-id="element-249" data-mp="20h7t0phm2znr">
                <div className="flex items-center justify-end gap-0.5" data-id="element-250" data-mp="20islm9kd88iz">
                  <AddToPlaylistMenu track={track} data-id="element-251" data-mp="130itj2g4w9hj" />
                  <button type="button" onClick={() => toggleFavorite(track)} aria-pressed={isFavorite(track.id)} aria-label={isFavorite(track.id) ? `Remove ${track.title} from saved` : `Save ${track.title}`} className="rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-iris-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-252" data-mp="18055z9o51lpa">
                    <HeartIcon className={`h-4 w-4 ${isFavorite(track.id) ? 'fill-iris-soft text-iris-soft' : ''}`} aria-hidden="true" data-id="element-253" data-mp="32cluk2z1j13j" />
                  </button>
                  {onRemove && <button type="button" onClick={() => onRemove(track)} aria-label={`Remove ${track.title} from this playlist`} className="rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-254" data-mp="2okxlk8k38sqs">
                      <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-255" data-mp="29xyvoi0vq6e6" />
                    </button>}
                </div>
              </td>
            </motion.tr>;
      })}
      </tbody>
    </table>;
}